import { useState } from "react";
import axios from "axios";
import { Mail, Send, CheckCircle2, Youtube, Heart, Facebook, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import BottomNav from "@/components/site/BottomNav";
import { MINISTRY } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setStatus({ state: "success", msg: "Thank you — your message has reached us. We will reply soon." });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      setStatus({ state: "error", msg: "Something went wrong. Please email us directly." });
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO — split, warm */}
      <section className="page-hero">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(110deg, var(--navy-900), var(--navy-700))",
          }}
        />
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.32em] text-gold-light mb-4">Get in Touch</div>
            <h1 className="font-display text-4xl lg:text-6xl text-cream leading-tight">
              We'd love to <span className="italic text-gold-light">hear from you.</span>
            </h1>
            <p className="mt-5 text-cream/85 text-lg max-w-md">
              Questions, encouragement, study requests, or testimonies of grace — write us anytime.
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-7 text-cream">
            <h3 className="font-display text-xl text-gold-light mb-4">Ministry Contact</h3>
            <a
              href={`mailto:${MINISTRY.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-3 text-cream hover:text-gold-light transition-colors"
            >
              <span className="w-10 h-10 rounded-full bg-gold text-navy-deep flex items-center justify-center"><Mail size={18} /></span>
              <span className="text-base break-all">{MINISTRY.email}</span>
            </a>
            <a
              href={MINISTRY.youtube}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-youtube-link"
              className="mt-4 flex items-center gap-3 text-cream hover:text-gold-light transition-colors"
            >
              <span className="w-10 h-10 rounded-full bg-gold text-navy-deep flex items-center justify-center"><Youtube size={18} /></span>
              <span className="text-base">{MINISTRY.youtubeHandle}</span>
            </a>
            <Link
              to="/prayer-requests"
              className="mt-4 flex items-center gap-3 text-cream hover:text-gold-light transition-colors"
              data-testid="contact-prayer-link"
            >
              <span className="w-10 h-10 rounded-full bg-gold text-navy-deep flex items-center justify-center"><Heart size={18} /></span>
              <span className="text-base">Submit a Prayer Request</span>
            </Link>
          </div>
        </div>
      </section>

      {/* MAIN: form + ministry message side by side */}
      <section className="py-20 lg:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl p-8 lg:p-10 border border-cream-200 shadow-[0_30px_80px_-40px_rgba(11,31,58,0.3)]">
              <div className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-2">Message Form</div>
              <h2 className="font-display text-3xl text-navy-deep mb-6">Send us a note</h2>

              {status.state === "success" ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <CheckCircle2 className="text-gold mx-auto mb-4" size={48} strokeWidth={1.5} />
                  <p className="text-navy-deep text-lg">{status.msg}</p>
                  <button onClick={() => setStatus({ state: "idle", msg: "" })} className="mt-6 btn-outline-navy px-5 py-2 rounded-full text-sm">
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">Name</label>
                      <input data-testid="contact-name" required value={form.name} onChange={(e) => update("name", e.target.value)} className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy mb-2">Email</label>
                      <input data-testid="contact-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Subject</label>
                    <input data-testid="contact-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none" placeholder="How can we help?" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Message</label>
                    <textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => update("message", e.target.value)} className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none resize-y" />
                  </div>
                  {status.state === "error" && <p className="text-red-600 text-sm" data-testid="contact-error">{status.msg}</p>}
                  <button data-testid="contact-submit" type="submit" disabled={status.state === "loading"} className="btn-primary w-full py-3.5 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
                    <Send size={16} /> {status.state === "loading" ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Side: encouragement + social */}
          <aside className="lg:col-span-2 flex flex-col gap-6">
            <div className="rounded-2xl p-8 text-cream" style={{ background: "linear-gradient(160deg, var(--navy-900), var(--navy-700))" }}>
              <h3 className="font-display text-2xl text-gold-light">A word of encouragement</h3>
              <p className="scripture mt-4 text-lg leading-relaxed">
                "Be careful for nothing; but in every thing by prayer and supplication with thanksgiving let your requests be made known unto God."
              </p>
              <p className="mt-3 text-xs text-cream/70 tracking-[0.3em] uppercase">— Philippians 4:6</p>
            </div>

            <div className="rounded-2xl p-7 bg-white border border-cream-200">
              <h3 className="font-display text-xl text-navy-deep mb-4">Follow & Connect</h3>
              <div className="flex flex-wrap gap-3">
                <a href={MINISTRY.youtube} target="_blank" rel="noreferrer" data-testid="contact-social-youtube" className="lift inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-cream-50 border border-cream-200 hover:border-gold text-navy text-sm">
                  <Youtube size={16} className="text-red-600" /> YouTube
                </a>
                <a href={`mailto:${MINISTRY.email}`} data-testid="contact-social-email" className="lift inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-cream-50 border border-cream-200 hover:border-gold text-navy text-sm">
                  <Mail size={16} className="text-gold" /> Email
                </a>
                <a href="#" data-testid="contact-social-facebook" className="lift inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-cream-50 border border-cream-200 hover:border-gold text-navy text-sm opacity-70">
                  <Facebook size={16} className="text-[#1877F2]" /> Facebook
                </a>
                <a href="#" data-testid="contact-social-instagram" className="lift inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-cream-50 border border-cream-200 hover:border-gold text-navy text-sm opacity-70">
                  <Instagram size={16} className="text-pink-500" /> Instagram
                </a>
              </div>
              <p className="mt-4 text-xs text-navy/55">
                Note: Facebook &amp; Instagram links can be added once your accounts are shared.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <BottomNav exclude={["/contact"]} />
    </div>
  );
}
